// src/app/_module/hooks/useNotifications.tsx
"use client";

import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

// Import the full notification types
import type { Notification as AppNotification } from "../types/notifications";
import { notificationService } from "../services/notificationService";
import AgencyTokenManager from "../services/agencyTokenManager";
import { realTimeService } from "../services/realTimeService";
import { showToast } from "../utils/show-toast";
import { getSupabaseSession } from "../services/auth";
import { useNotificationSound, useNotificationSoundPreference } from "./use-notification-sound";

interface NotificationsContextType {
  notifications: AppNotification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  hasMore: boolean;
  hasNewNotifications: boolean;
  fetchNotifications: (page?: number) => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => void;
  dismissNotification: (id: string) => Promise<void>;
  dismissAllNotifications: () => Promise<void>;
  loadMore: () => Promise<void>;
  refetch: () => Promise<void>;
  clearNewNotificationIndicator: () => void;
}

// Create context with explicit React.createContext
const NotificationsContext = React.createContext<NotificationsContextType | undefined>(undefined);

// Polling interval — fallback only. SSE realtime handles instant delivery.
const POLLING_INTERVAL = 60000; // safety net; Supabase Realtime (below) delivers instantly once the RLS policy exists

export function NotificationsProvider({ children }: { children: React.ReactNode }) {
  const [currentPage, setCurrentPage] = React.useState(1);
  const [readNotifications, setReadNotifications] = React.useState<Set<string>>(new Set());
  const [hasNewNotifications, setHasNewNotifications] = React.useState(false);
  const previousNotificationIdsRef = React.useRef<Set<string>>(new Set());
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const [activeAgencyId, setActiveAgencyId] = React.useState<string | null>(null);
  const [accumulatedNotifications, setAccumulatedNotifications] = React.useState<AppNotification[]>([]);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  // Use a ref to track if user has loaded more pages - refs don't have stale closure issues
  const hasLoadedMoreRef = React.useRef(false);
  const queryClient = useQueryClient();

  // Notification sound functionality
  const { getSoundPreference } = useNotificationSoundPreference();
  const [soundEnabled, setSoundEnabled] = React.useState(getSoundPreference());
  const { playSound } = useNotificationSound({ enabled: soundEnabled });

  // Listen for changes to sound preference from settings
  React.useEffect(() => {
    const handlePreferenceChange = (event: CustomEvent) => {
      setSoundEnabled(event.detail.enabled);
    };

    window.addEventListener('notificationSoundPreferenceChanged', handlePreferenceChange as EventListener);

    return () => {
      window.removeEventListener('notificationSoundPreferenceChanged', handlePreferenceChange as EventListener);
    };
  }, []);

  // Check authentication status using Supabase session
  React.useEffect(() => {
    const checkAuth = async () => {
      try {
        const session = await getSupabaseSession();
        setIsAuthenticated(!!session?.access_token);
      } catch {
        setIsAuthenticated(false);
      }
      // Track the active agency so notifications stay scoped to it. Picking
      // this up here (rather than once on mount) means switching agencies
      // re-keys the query and refetches, instead of serving the previous
      // agency's cached notifications.
      setActiveAgencyId(AgencyTokenManager.getCurrentAgencyId());
    };

    checkAuth();
    // Re-check auth periodically (every 30 seconds to match polling)
    const interval = setInterval(checkAuth, 30000);
    return () => clearInterval(interval);
  }, []);

  const hasAuthToken = React.useCallback(() => {
    return isAuthenticated;
  }, [isAuthenticated]);

  // Use React Query for polling notifications (always fetch page 1 for polling)
  const {
    data: notificationsData,
    isLoading,
    error,
    refetch: refetchQuery,
  } = useQuery({
    queryKey: ["notifications", activeAgencyId],
    queryFn: async () => {
      if (!hasAuthToken()) {
        return { items: [], meta: { totalPages: 1, page: 1 } };
      }
      return await notificationService.getNotifications(1, 20, activeAgencyId ?? undefined);
    },
    enabled: hasAuthToken(),
    // Poll every 30 seconds for new notifications
    refetchInterval: POLLING_INTERVAL,
    // Only poll when browser tab is active
    refetchIntervalInBackground: false,
    // Keep previous data while fetching new
    placeholderData: (previousData) => previousData,
    // Stale time set to 25 seconds (slightly less than polling interval)
    staleTime: 25000,
  });

  // Update accumulated notifications when data loads
  // Only replace page 1 items when we haven't loaded more pages yet
  // When we have loaded more, merge new page 1 items at the beginning while keeping older pages
  React.useEffect(() => {
    if (!notificationsData?.items) return;

    setAccumulatedNotifications(prev => {
      // If we haven't accumulated anything yet, just use the new data
      if (prev.length === 0) {
        return notificationsData.items;
      }

      // If user hasn't loaded more pages yet, just use the new data
      // Use ref instead of state to avoid stale closure issues during polling
      if (!hasLoadedMoreRef.current) {
        return notificationsData.items;
      }

      // We've loaded more pages - merge new page 1 items with existing accumulated items
      // Replace the first page's worth of items with fresh data, keep the rest
      const page1Ids = new Set(notificationsData.items.map(n => n.id));
      const olderPageItems = prev.filter(n => !page1Ids.has(n.id));

      // New page 1 items + items from pages 2+ that we loaded previously
      return [...notificationsData.items, ...olderPageItems];
    });
  }, [notificationsData]);

  // Process notifications with read status and deduplicate by ID, reminderId, and leaderboard type
  const notifications = React.useMemo(() => {
    // Use accumulated notifications (includes loaded more pages)
    const sourceItems = accumulatedNotifications.length > 0 ? accumulatedNotifications : (notificationsData?.items || []);
    if (sourceItems.length === 0) return [];

    // Deduplicate notifications by ID AND by subject+date combination
    // This prevents showing multiple notifications for the same event
    const seenIds = new Set<string>();
    const seenReminderIds = new Set<string>();
    const seenSubjectDateKeys = new Set<string>(); // Generic deduplication by subject + date

    const uniqueNotifications = sourceItems.filter(notification => {
      const metadata = notification.metadata as Record<string, unknown> | undefined;
      const notificationType = metadata?.type as string | undefined;
      const subject = notification.subject?.toLowerCase().trim() || '';

      // FILTER OUT "incoming_call" notifications - only show "missed_call" for call-related
      // Incoming call notifications are handled by IncomingCallWidget, not the notification bell
      if (notificationType === 'incoming_call') {
        return false;
      }

      // Skip if we've already seen this notification ID
      if (seenIds.has(notification.id)) {
        return false;
      }

      // For reminder notifications, deduplicate by reminderId in metadata
      const reminderId = metadata?.reminderId as string;
      if (reminderId) {
        if (seenReminderIds.has(reminderId)) {
          return false; // Skip duplicate reminder notification
        }
        seenReminderIds.add(reminderId);
      }

      // Generic deduplication: same subject on the same day = duplicate.
      // Catches weekly reports, leaderboard notifications, and so on.
      //
      // SKIP missed_call notifications here — every missed call has the SAME
      // subject ("Missed Call") so this dedup would collapse every missed
      // call from different callers on the same day into one row, hiding
      // legitimate incoming calls from agents. Missed calls are deduped/
      // grouped downstream in DashboardHeader by phone number + 60s window
      // ("×N redials" badge) which correctly distinguishes different callers.
      if (subject && notificationType !== 'missed_call') {
        const notificationDate = new Date(notification.createdAt).toDateString();
        const subjectKey = `${subject}:${notificationDate}`;
        if (seenSubjectDateKeys.has(subjectKey)) {
          return false; // Skip duplicate notification with same subject on same day
        }
        seenSubjectDateKeys.add(subjectKey);
      }

      seenIds.add(notification.id);
      return true;
    });

    // Read = marked in this session OR already read on the server. The local
    // set alone forgot everything on relaunch / on the next device, so "Read
    // all" came back unread after every refetch.
    return uniqueNotifications.map(notification => ({
      ...notification,
      isRead:
        readNotifications.has(notification.id) ||
        !!(notification as any).readAt ||
        !!(notification as any).metadata?.readAt,
    }));
  }, [accumulatedNotifications, notificationsData, readNotifications]);

  const totalPages = notificationsData?.meta.totalPages ?? 1;
  const unreadCount = notifications.filter(n => !n.isRead).length;
  const hasMore = currentPage < totalPages;

  // Detect new notifications and show visual indicator + play sound
  React.useEffect(() => {
    if (notifications.length === 0) return;

    const currentNotificationIds = new Set(notifications.map(n => n.id));
    const previousIds = previousNotificationIdsRef.current;

    // Check for new notifications by comparing IDs
    const newNotifications = notifications.filter(
      n => !previousIds.has(n.id) && !n.isRead
    );

    if (newNotifications.length > 0 && previousIds.size > 0) {
      setHasNewNotifications(true);

      // Play notification sound for new notifications
      playSound();

      // Show toast for important notifications. Reminders, appointments,
      // and anything explicitly flagged as high-priority via metadata
      // (e.g., "No Active Phone Number" from the dialer path) should
      // surface immediately so the user actually sees the message,
      // not just a silent badge in the bell.
      newNotifications.forEach(notification => {
        const isReminder = notification.subject?.toLowerCase().includes('reminder') ||
                          notification.bodyText?.toLowerCase().includes('reminder');
        const isAppointment = notification.subject?.toLowerCase().includes('appointment');
        const meta = notification.metadata as Record<string, unknown> | undefined;
        const isHighPriority = meta?.priority === 'high' || meta?.toast === true;
        const reason = typeof meta?.reason === 'string' ? meta.reason : '';
        const isDialerBlocker = reason === 'no_active_caller_id';
        // Numbers finished moving to another InsuraDialer system: swap the device now, not at the next token refresh.
        if (reason === 'dialer_system_changed') window.dispatchEvent(new Event('dialer:carrier-check'));

        if (isReminder || isAppointment || isHighPriority || isDialerBlocker) {
          showToast(
            notification.subject || "New notification",
            isDialerBlocker ? "error" : "info",
            {
              description: notification.bodyText?.substring(0, 140),
              duration: isDialerBlocker ? 8000 : 5000,
            }
          );
        }
      });
    }

    // Update the ref (doesn't trigger re-render)
    previousNotificationIdsRef.current = currentNotificationIds;
  }, [notifications, playSound]);

  // Listen to real-time notification events
  React.useEffect(() => {
    if (!hasAuthToken()) return;

    const handleNotificationEvent = () => {
      // Invalidate and refetch notifications immediately
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      setHasNewNotifications(true);

      // Play sound for real-time notifications
      playSound();
    };

    // Subscribe to real-time notification events
    realTimeService.addEventListener("notifications:created", handleNotificationEvent);
    realTimeService.addEventListener("notifications:updated", handleNotificationEvent);

    // Supabase Realtime on the notifications table itself — the SSE service
    // above is never actually connected (EventSource can't carry the Bearer
    // token the stream route requires), so without this the bell is a 2-minute
    // poll. Delivers the moment a row lands, once `notifications` has a SELECT
    // policy for `authenticated` (user_id = auth.uid()); until then the channel
    // stays quiet and polling remains the floor.
    let mounted = true;
    let client: any = null;
    let channel: any = null;
    (async () => {
      try {
        const { getSupabaseClient } = await import("@/app/_module/services/supabaseClient");
        client = await getSupabaseClient();
        const { data } = await client.auth.getSession();
        const uid = data?.session?.user?.id;
        if (!mounted || !client || !uid) return;
        channel = client
          .channel(`notifications-live-${uid}`)
          .on("postgres_changes", { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${uid}` }, handleNotificationEvent)
          .on("postgres_changes", { event: "UPDATE", schema: "public", table: "notifications", filter: `user_id=eq.${uid}` }, () => {
            queryClient.invalidateQueries({ queryKey: ["notifications"] });
          })
          .subscribe();
      } catch {
        /* realtime unavailable — polling still covers it */
      }
    })();

    return () => {
      mounted = false;
      realTimeService.removeEventListener("notifications:created", handleNotificationEvent);
      realTimeService.removeEventListener("notifications:updated", handleNotificationEvent);
      try { if (channel && client) client.removeChannel(channel); } catch { /* ignore */ }
    };
  }, [hasAuthToken, queryClient, playSound]);

  const fetchNotifications = React.useCallback(async (page = 1) => {
    setCurrentPage(page);
    await refetchQuery();
  }, [refetchQuery]);

  const markAsRead = React.useCallback(async (id: string) => {
    try {
      await notificationService.markAsRead(id);
      setReadNotifications(prev => new Set([...prev, id]));

      // Update the query cache immediately. Prefix-match so it hits the
      // agency-scoped key (["notifications", activeAgencyId]).
      queryClient.setQueriesData({ queryKey: ["notifications"] }, (oldData: any) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          items: oldData.items.map((n: AppNotification) =>
            n.id === id ? { ...n, isRead: true } : n
          ),
        };
      });
    } catch (err) {
      // Silently handle errors
    }
  }, [queryClient]);

  const markAllAsRead = React.useCallback(() => {
    const unreadIds = notifications
      .filter(n => !n.isRead)
      .map(n => n.id);

    setReadNotifications(prev => new Set([...prev, ...unreadIds]));

    // Update query cache immediately (prefix-match the agency-scoped key)
    queryClient.setQueriesData({ queryKey: ["notifications"] }, (oldData: any) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        items: oldData.items.map((n: AppNotification) => ({ ...n, isRead: true })),
      };
    });

    // Mark as read on backend
    unreadIds.forEach(id => {
      notificationService.markAsRead(id).catch(() => {
        // Silently handle errors - marking as read is not critical
      });
    });
  }, [notifications, queryClient]);

  const dismissNotification = React.useCallback(async (id: string) => {
    try {
      // Optimistically remove from accumulated notifications
      setAccumulatedNotifications(prev => prev.filter(n => n.id !== id));

      // Also update the query cache (prefix-match the agency-scoped key)
      queryClient.setQueriesData({ queryKey: ["notifications"] }, (oldData: any) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          items: oldData.items.filter((n: AppNotification) => n.id !== id),
        };
      });

      // Remove from local read tracking
      setReadNotifications(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });

      // Call backend API
      await notificationService.dismissNotification(id);
    } catch (err) {
      // Refetch to restore state on error
      await refetchQuery();
    }
  }, [queryClient, refetchQuery]);

  const dismissAllNotifications = React.useCallback(async () => {
    try {
      // Optimistically clear accumulated notifications
      setAccumulatedNotifications([]);

      // Also clear from query cache (prefix-match the agency-scoped key)
      queryClient.setQueriesData({ queryKey: ["notifications"] }, (oldData: any) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          items: [],
        };
      });

      // Clear local read tracking
      setReadNotifications(new Set());

      // Reset page and loaded more flag
      setCurrentPage(1);
      hasLoadedMoreRef.current = false;

      // Call backend API
      await notificationService.dismissAllNotifications();
    } catch (err) {
      // Refetch to restore state on error
      await refetchQuery();
    }
  }, [queryClient, refetchQuery]);

  const loadMore = React.useCallback(async () => {
    if (currentPage < totalPages && !isLoading && !isLoadingMore) {
      setIsLoadingMore(true);
      try {
        const nextPage = currentPage + 1;
        const response = await notificationService.getNotifications(nextPage, 20, activeAgencyId ?? undefined);

        if (response?.items) {
          // Mark that user has loaded more pages - this prevents polling from resetting
          hasLoadedMoreRef.current = true;

          // Append new notifications to accumulated list
          setAccumulatedNotifications(prev => {
            // Deduplicate by ID when appending
            const existingIds = new Set(prev.map(n => n.id));
            const newItems = response.items.filter(n => !existingIds.has(n.id));
            return [...prev, ...newItems];
          });
          setCurrentPage(nextPage);
        }
      } catch (err) {
        console.error('Failed to load more notifications:', err);
      } finally {
        setIsLoadingMore(false);
      }
    }
  }, [currentPage, totalPages, isLoading, isLoadingMore, activeAgencyId]);

  const refetch = React.useCallback(async () => {
    // Only refetch page 1 data - don't reset accumulated or loaded more flag
    // This preserves the "load more" state when user reopens the dropdown
    await refetchQuery();
  }, [refetchQuery]);

  const clearNewNotificationIndicator = React.useCallback(() => {
    setHasNewNotifications(false);
  }, []);

  const value: NotificationsContextType = {
    notifications,
    unreadCount,
    isLoading: isLoading || isLoadingMore,
    error: error ? "Failed to fetch notifications" : null,
    currentPage,
    totalPages,
    hasMore,
    hasNewNotifications,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    dismissNotification,
    dismissAllNotifications,
    loadMore,
    refetch,
    clearNewNotificationIndicator,
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications(): NotificationsContextType {
  const context = React.useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationsProvider');
  }
  return context;
}
